import React, { useEffect, createElement } from 'react';
import { ArrowRightIcon, ZapIcon, SmartphoneIcon, SearchIcon, ShieldCheckIcon, CalendarIcon, MonitorIcon, CodeIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteTipsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website tips | Verbeter je website | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Praktische website tips voor ondernemers. Verbeter je conversie, snelheid en vindbaarheid met deze bewezen tips. Door de webdesigners van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website tips | Verbeter je website | Noord',
      'og:description': 'Praktische website tips voor ondernemers. Verbeter je conversie, snelheid en vindbaarheid met deze bewezen tips.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-tips',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2742">
      {/* Hero */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2743">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2744">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2745" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2746" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2747" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2748">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2749">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2750">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2751">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2752">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2753">/</span>
            <span className="text-accent-light font-medium" data-id="element-2754">Website tips</span>
          </nav>
          <a href="#/kennisbank" className="inline-flex items-center gap-2 text-accent-light font-semibold text-sm tracking-widest uppercase mb-4 hover:text-white transition-colors animate-fade-in-up" data-id="element-2755">
            ← Kennisbank
          </a>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-2756">
            Website tips voor bedrijven
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto animate-fade-in-up-delay-2" data-id="element-2757">
            Praktische tips voor betere websites. Verbeter je online
            aanwezigheid en trek meer klanten aan.
          </p>
        </div>
      </section>

      {/* Tip 1 — Snelheid */}
      <section className="py-24 bg-surface-muted" data-id="element-2758">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2759">
          <div className="flex items-start gap-6" data-id="element-2760">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2761">
              <ZapIcon className="w-7 h-7 text-accent" data-id="element-2762" />
            </div>
            <div data-id="element-2763">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2764">
                Tip 1
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2765">
                Zorg dat je website snel laadt
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2766">
                Snelheid is een van de belangrijkste factoren voor een
                succesvolle website. Bezoekers verwachten dat een pagina binnen
                2 seconden laadt. Duurt het langer? Dan haakt meer dan 50% af
                voordat ze je content hebben gezien.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2767">
                Google gebruikt laadsnelheid als rankingfactor. Een snelle
                website scoort dus beter in de zoekresultaten. Optimaliseer je
                afbeeldingen, gebruik caching en kies voor betrouwbare hosting
                om je website snel te houden.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2768">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2769">
                  💡 Tip: Test je website snelheid gratis via Google PageSpeed
                  Insights en verbeter de punten die het meeste impact hebben.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tip 2 — Mobiel */}
      <section className="py-24 bg-white" data-id="element-2770">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2771">
          <div className="flex items-start gap-6" data-id="element-2772">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2773">
              <SmartphoneIcon className="w-7 h-7 text-accent" data-id="element-2774" />
            </div>
            <div data-id="element-2775">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2776">
                Tip 2
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2777">
                Maak je website mobielvriendelijk
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2778">
                Meer dan 60% van alle websitebezoeken komt tegenwoordig via een
                smartphone. Als je website niet goed werkt op mobiel, verlies je
                het grootste deel van je potentiële klanten.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2779">
                Een mobielvriendelijke website past zich automatisch aan het
                schermformaat aan. Knoppen zijn groot genoeg om op te tikken,
                tekst is leesbaar zonder te zoomen en formulieren zijn eenvoudig
                in te vullen op een klein scherm.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2780">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2781">
                  💡 Tip: Bekijk je eigen website op je telefoon. Kun je alles
                  makkelijk lezen en aanklikken? Zo niet, dan is het tijd voor
                  een update.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tip 3 — SEO */}
      <section className="py-24 bg-surface-muted" data-id="element-2782">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2783">
          <div className="flex items-start gap-6" data-id="element-2784">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2785">
              <SearchIcon className="w-7 h-7 text-accent" data-id="element-2786" />
            </div>
            <div data-id="element-2787">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2788">
                Tip 3
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2789">
                Investeer in SEO vanaf dag één
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2790">
                Zoekmachineoptimalisatie (SEO) is geen luxe, maar een noodzaak.
                Als je website niet vindbaar is in Google, missen potentiële
                klanten je bedrijf. SEO zorgt ervoor dat je structureel gevonden
                wordt door mensen die actief zoeken naar jouw diensten.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2791">
                Begin met de basis: gebruik relevante zoekwoorden in je
                paginatitels en teksten, schrijf unieke meta-beschrijvingen en
                zorg voor een logische sitestructuur. Technische SEO — zoals
                snelle laadtijden en een veilige verbinding (SSL) — is minstens
                zo belangrijk.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2792">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2793">
                  💡 Tip: Focus op lokale SEO als je een lokaal bedrijf hebt.
                  Vermeld je vestigingsplaats in titels en teksten, en claim je
                  Google Bedrijfsprofiel.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tip 4 — Vertrouwen */}
      <section className="py-24 bg-white" data-id="element-2794">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2795">
          <div className="flex items-start gap-6" data-id="element-2796">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2797">
              <ShieldCheckIcon className="w-7 h-7 text-accent" data-id="element-2798" />
            </div>
            <div data-id="element-2799">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2800">
                Tip 4
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2801">
                Bouw vertrouwen op met je website
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2802">
                Bezoekers beslissen binnen enkele seconden of ze jouw website
                vertrouwen. Een professioneel design, duidelijke contactgegevens
                en sociale bewijskracht (reviews, logo's van klanten) maken het
                verschil.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2803">
                Zorg voor een duidelijke over-ons pagina, toon je telefoonnummer
                en adres zichtbaar en gebruik echte foto's in plaats van
                stockfoto's. Een SSL-certificaat (het groene slotje) is
                tegenwoordig een absolute must voor het vertrouwen van bezoekers
                én Google.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2804">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2805">
                  💡 Tip: Vraag tevreden klanten om een review en plaats deze
                  zichtbaar op je website. Niets overtuigt beter dan de ervaring
                  van anderen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde artikelen */}
      <section className="py-24 bg-surface-muted" data-id="element-2806">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2807">
          <div className="text-center mb-12" data-id="element-2808">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2809">
              Gerelateerde artikelen
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-2810">
            <a href="#/kosten-website-laten-maken" className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300" data-id="element-2811">
              <div className="aspect-[16/10] overflow-hidden" data-id="element-2812">
                <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop" alt="Kosten website laten maken" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2813" />
              </div>
              <div className="p-6" data-id="element-2814">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2" data-id="element-2815">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-2816" />
                  15 feb 2026
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-accent transition-colors" data-id="element-2817">
                  Kosten website laten maken
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2818">
                  Wat kost een professionele website in 2026? Wij zetten de
                  prijzen op een rij.
                </p>
                <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm mt-3 group-hover:gap-2.5 transition-all" data-id="element-2819">
                  Lees meer
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2820" />
                </span>
              </div>
            </a>

            <a href="#/website-laten-maken-checklist" className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300" data-id="element-2821">
              <div className="aspect-[16/10] overflow-hidden" data-id="element-2822">
                <img src="https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=600&h=400&fit=crop" alt="Website laten maken checklist" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2823" />
              </div>
              <div className="p-6" data-id="element-2824">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2" data-id="element-2825">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-2826" />8 feb 2026
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-accent transition-colors" data-id="element-2827">
                  Website laten maken checklist
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2828">
                  Alles wat je moet weten voordat je een website laat maken.
                </p>
                <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm mt-3 group-hover:gap-2.5 transition-all" data-id="element-2829">
                  Lees meer
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2830" />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde diensten */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-2831">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-2832" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2833">
          <div className="text-center mb-12" data-id="element-2834">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2835">
              Gerelateerde diensten
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-2836">
            <a href="#/website-laten-maken" className="group bg-white/[0.06] border border-white/10 rounded-2xl p-8 hover:bg-white/[0.1] transition-all duration-300" data-id="element-2837">
              <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mb-5" data-id="element-2838">
                <MonitorIcon className="w-6 h-6 text-accent" data-id="element-2839" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-2840">
                Website laten maken
              </h3>
              <p className="text-navy-300 text-sm mb-4" data-id="element-2841">
                Professionele websites die klanten opleveren. Van strategie tot
                oplevering.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-2842">
                Bekijk dienst
                <ArrowRightIcon className="w-4 h-4" data-id="element-2843" />
              </span>
            </a>

            <a href="#/wordpress-websites" className="group bg-white/[0.06] border border-white/10 rounded-2xl p-8 hover:bg-white/[0.1] transition-all duration-300" data-id="element-2844">
              <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mb-5" data-id="element-2845">
                <CodeIcon className="w-6 h-6 text-accent" data-id="element-2846" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-2847">
                WordPress websites
              </h3>
              <p className="text-navy-300 text-sm mb-4" data-id="element-2848">
                Flexibele WordPress sites met onbeperkte mogelijkheden en
                eenvoudig beheer.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-2849">
                Bekijk dienst
                <ArrowRightIcon className="w-4 h-4" data-id="element-2850" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde links strip */}
      <section className="py-12 bg-white border-b border-navy-100" data-id="element-2851">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2852">
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2853">
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2854">
              Homepage
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2855">•</span>
            <a href="#/kennisbank" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2856">
              Kennisbank
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2857" />
    </main>;
}